import { isMatch, merge, pick } from 'lodash';
import qs from 'qs';
import { useCallback, useMemo } from 'react';
import { useHistory, useLocation, useParams } from 'react-router';

import usePersistent from 'hooks/use-persistent';
import { ROUTES } from 'pages/Search/constants';
import { noneIf } from 'pages/Search/utils';
import normalizeSearch from 'utils/normalize-search';

interface Payload {
    page: number;
    size: number;
    letter?: string;
    sort?: {
        field: 'vendor' | 'product' | 'cves';
        direction: 'asc' | 'desc';
    };
    search: string;
}

const defaults: Payload = {
    page: 1,
    size: 20,
    letter: undefined,
    sort: {
        field: 'cves',
        direction: 'desc'
    },
    search: ''
};

export function useProductCatalogPayload() {
    const history = useHistory();
    const location = useLocation();
    const parameters = useParams<{ letter: string; page: string }>();
    const query = useMemo(() => qs.parse(location.search, { ignoreQueryPrefix: true }), [location.search]);

    const page = parameters.page ? parseInt(parameters.page) : defaults.page;
    const letter = parameters.letter ? parameters.letter : defaults.letter;
    const size = query.size ? parseInt(query.size.toString()) : defaults.size;
    const search = query.search ? normalizeSearch(query.search.toString()) : defaults.search;
    const sort = query.sort || defaults.sort;

    const payload = useMemo(
        () => ({ page, letter, size, search, sort } as Payload),
        [letter, page, search, size, sort]
    );
    const payloadRef = usePersistent(payload);

    const url = useCallback(
        (patch: Partial<Payload>) => {
            const previous = payloadRef.current;
            const current = merge({}, previous, patch);

            return ROUTES.PRODUCTS({
                page: isMatch(previous, pick(current, ['letter', 'search', 'size']))
                    ? noneIf(current.page, defaults.page)
                    : undefined,
                search: current.search ? noneIf(normalizeSearch(current.search), defaults.search) : undefined,
                sort: noneIf(current.sort, defaults.sort),
                letter: noneIf(current.letter, defaults.letter),
                size: noneIf(current.size, previous.size)
            });
        },
        [payloadRef]
    );

    const set = useCallback((patch: Partial<Payload>) => history.push(url(patch)), [history, url]);

    const actions = useMemo(() => ({ url, set }), [set, url]);

    return [payload, actions] as const;
}
